import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { LygoLogo } from '@/components/LygoLogo';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setMessage('');

    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: t('auth.forgotPassword.error') }));
        throw new Error(data.error || t('auth.forgotPassword.error'));
      }

      setMessage(t('auth.forgotPassword.success'));
    } catch (submitError) {
      const messageText = submitError instanceof Error ? submitError.message : t('auth.forgotPassword.error');
      setError(messageText);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center p-8 bg-[radial-gradient(circle_at_15%_15%,hsl(193_62%_88%),transparent_34%),radial-gradient(circle_at_84%_88%,hsl(38_85%_90%),transparent_30%)]">
      <Card className="w-full max-w-[460px] border-primary/15 shadow-[0_14px_40px_rgba(15,23,42,0.12)]">
        <CardHeader className="space-y-1">
          <LygoLogo />
          <CardTitle className="text-2xl font-heading pt-1">{t('auth.brandName')}</CardTitle>
          <CardDescription>{t('auth.forgotPassword.title')}</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {message && (
            <Alert variant="success" className="mb-4">
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>{message}</AlertDescription>
            </Alert>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.emailLabel')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('auth.emailPlaceholder')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? t('auth.forgotPassword.submitting') : t('auth.forgotPassword.submit')}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <Link to="/login" className="text-primary hover:underline">{t('auth.forgotPassword.backToLogin')}</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
